import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { MusicControl } from "./components/MusicControl";
import { MatchFlowScreen, MatchInviteScreen, MatchResultScreen } from "./screens/MatchScreens";
import { ALBUMS } from "./data/albums";
import { clearSelections, isSelectionComplete, loadSelections, saveSelections, selectedSongs } from "./logic/selection";
import { AlbumScreen } from "./screens/AlbumScreen";
import { AlbumCloseupScreen } from "./screens/AlbumCloseupScreen";
import { GuideScreen } from "./screens/GuideScreen";
import { HomeScreen } from "./screens/HomeScreen";
import { ResultScreen } from "./screens/ResultScreen";
import { ReviewScreen } from "./screens/ReviewScreen";
import type { SelectionMap } from "./types";

type Route =
  | { name: "home" }
  | { name: "guide" }
  | { name: "album"; index: number }
  | { name: "closeup"; index: number }
  | { name: "review" }
  | { name: "result" }
  | { name: "result-songs" }
  | { name: "result-card" }
  | { name: "match-invite" }
  | { name: "match"; ownerCode: string }
  | { name: "match-result"; ownerCode: string; friendCode: string };

function readRoute(): Route {
  const hash = window.location.hash.replace(/^#/, "") || "/";
  const albumMatch = hash.match(/^\/album\/(\d+)$/);
  if (albumMatch) {
    const index = Number(albumMatch[1]) - 1;
    if (index >= 0 && index < ALBUMS.length) return { name: "album", index };
  }
  const closeupMatch = hash.match(/^\/closeup\/(\d+)$/);
  if (closeupMatch) {
    const index = Number(closeupMatch[1]) - 1;
    if (index >= 0 && index < ALBUMS.length) return { name: "closeup", index };
  }
  if (hash === "/match/invite") return { name: "match-invite" };
  const matchResult = hash.match(/^\/match-result\/([^/]+)\/([^/]+)$/);
  if (matchResult) return { name: "match-result", ownerCode: matchResult[1], friendCode: matchResult[2] };
  const friendMatch = hash.match(/^\/match\/([^/]+)$/);
  if (friendMatch) return { name: "match", ownerCode: friendMatch[1] };
  if (hash === "/guide") return { name: "guide" };
  if (hash === "/review") return { name: "review" };
  if (hash === "/result/card") return { name: "result-card" };
  if (hash === "/result/songs") return { name: "result-songs" };
  if (hash === "/result") return { name: "result" };
  return { name: "home" };
}

function navigate(path: string) {
  window.location.hash = path;
  window.scrollTo({ top: 0, behavior: "instant" });
}

export default function App() {
  const [route, setRoute] = useState<Route>(readRoute);
  const [selections, setSelections] = useState<SelectionMap>(loadSelections);
  useEffect(() => {
    const onHashChange = () => setRoute(readRoute());
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);
  useEffect(() => saveSelections(selections), [selections]);
  const chosenSongs = useMemo(() => selectedSongs(selections), [selections]);
  const restart = useCallback(() => {
    clearSelections();
    setSelections({});
    navigate("/");
  }, []);
  const withMusic = (page: ReactNode) => <><MusicControl />{page}</>;

  if ((route.name === "review" || route.name === "result" || route.name === "result-songs" || route.name === "result-card" || route.name === "match-invite") && !isSelectionComplete(selections)) {
    const firstMissing = ALBUMS.findIndex((album) => !selections[album.name]);
    navigate(`/album/${Math.max(0, firstMissing) + 1}`);
    return withMusic(null);
  }

  if (route.name === "match-invite") return withMusic(<MatchInviteScreen selections={selections} onBack={() => navigate("/result/card")} />);
  if (route.name === "match") return withMusic(<MatchFlowScreen ownerCode={route.ownerCode} onNavigate={navigate} onBack={() => navigate("/")} />);
  if (route.name === "match-result") return withMusic(<MatchResultScreen ownerCode={route.ownerCode} friendCode={route.friendCode} onBack={() => navigate("/")} />);
  if (route.name === "home") return withMusic(<HomeScreen onStart={() => { clearSelections(); setSelections({}); navigate("/guide"); }} />);
  if (route.name === "guide") return withMusic(<GuideScreen onContinue={() => navigate("/album/1")} onBack={() => navigate("/")} />);
  if (route.name === "album") {
    const album = ALBUMS[route.index];
    return withMusic(
      <AlbumScreen
        index={route.index}
        selected={selections[album.name]}
        onSelect={(song) => setSelections((current) => ({ ...current, [album.name]: song }))}
        onNext={() => navigate(`/closeup/${route.index + 1}`)}
        onBack={() => navigate(route.index === 0 ? "/guide" : `/album/${route.index}`)}
      />,
    );
  }
  if (route.name === "closeup") {
    const album = ALBUMS[route.index];
    const selected = selections[album.name];
    if (!selected) {
      navigate(`/album/${route.index + 1}`);
      return null;
    }
    return withMusic(
      <AlbumCloseupScreen
        index={route.index}
        selected={selected}
        onBack={() => navigate(`/album/${route.index + 1}`)}
        onConfirm={() => navigate(route.index === ALBUMS.length - 1 ? "/review" : `/album/${route.index + 2}`)}
      />,
    );
  }
  if (route.name === "review") return withMusic(<ReviewScreen selections={selections} onEdit={(index) => navigate(`/album/${index + 1}`)} onConfirm={() => navigate("/result")} onBack={() => navigate("/album/9")} />);
  return withMusic(<ResultScreen route={route.name} songs={chosenSongs} onRestart={restart} onNavigate={navigate} />);
}
